import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only clear auth if not on a login-related or session-related request
      const url = error.config?.url || '';
      const isAuthUrl = url.includes('/auth/login') || url.includes('/auth/register') || url.includes('/auth/me');
      const isSessionUrl = url.includes('/sessions/');
      if (!isAuthUrl && !isSessionUrl) {
        // Don't auto-logout students (they use PIN login, not email)
        const savedStudent = localStorage.getItem('student');
        if (savedStudent) {
          // Student session — don't redirect, just reject
          return Promise.reject(error);
        }
        // If impersonating, restore admin session instead of logging out
        const origToken = localStorage.getItem('admin_original_token');
        const origUser = localStorage.getItem('admin_original_user');
        if (origToken && origUser) {
          localStorage.setItem('token', origToken);
          localStorage.setItem('user', origUser);
          localStorage.removeItem('admin_original_token');
          localStorage.removeItem('admin_original_user');
          window.location.href = '/admin';
          return Promise.reject(error);
        }
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH API ====================

export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  studentLogin: (studentCode, pin) => apiClient.post('/auth/student-login', { student_code: studentCode, pin }),
  getMe: () => apiClient.get('/auth/me'),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  changePassword: (currentPassword, newPassword) => apiClient.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword }),
  resetPassword: (data) => apiClient.post('/auth/reset-password', data),
  sendVerification: () => apiClient.post('/auth/send-verification'),
  verifyEmail: (code) => apiClient.post('/auth/verify-email', { code }),
  getEnrollment: (code) => apiClient.get(`/auth/enrollment/${code}`),
  claimEnrollment: (data) => apiClient.post('/auth/claim-enrollment', data),
  getProfile: () => apiClient.get('/auth/profile'),
  updateProfile: (data) => apiClient.patch('/auth/profile', data),
  getTeam: () => apiClient.get('/auth/team'),
  inviteTeamMember: (data) => apiClient.post('/auth/team/invite', data),
  removeTeamMember: (id) => apiClient.delete(`/auth/team/${id}`),
};

// ==================== STUDENT API ====================

const audioExt = (blob) => {
  const t = (blob.type || '').toLowerCase();
  return t.includes('mp4') ? 'm4a' : t.includes('ogg') ? 'ogg' : t.includes('wav') ? 'wav' : t.includes('mpeg') ? 'mp3' : 'webm';
};

export const writingAPI = {
  current: () => apiClient.get('/writing/current', { timeout: 60000 }),
  submit: (data) => apiClient.post('/writing/submit', data, { timeout: 90000 }),
  history: (limit = 10) => apiClient.get('/writing/history', { params: { limit } }),
  forStudent: (studentId, limit = 20) => apiClient.get(`/students/${studentId}/writing`, { params: { limit } }),
  // dictation and spoken follow-ups (audio is kept for the parent)
  dictate: (taskId, blob, seconds) => {
    const fd = new FormData();
    fd.append('task_id', taskId);
    fd.append('seconds', String(Math.round(seconds || 0)));
    fd.append('audio', blob, `clip.${audioExt(blob)}`);
    return apiClient.post('/writing/dictate', fd, { timeout: 90000, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  followup: (submissionId) => apiClient.get(`/writing/submissions/${submissionId}/followup`, { timeout: 60000 }),
  followupAnswer: (submissionId, qIndex, blob, seconds) => {
    const fd = new FormData();
    fd.append('q_index', String(qIndex));
    fd.append('seconds', String(Math.round(seconds || 0)));
    fd.append('audio', blob, `clip.${audioExt(blob)}`);
    return apiClient.post(`/writing/submissions/${submissionId}/followup/answer`, fd, { timeout: 90000, headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // parent controls
  settings: (studentId) => apiClient.get(`/students/${studentId}/writing-settings`),
  saveSettings: (studentId, data) => apiClient.put(`/students/${studentId}/writing-settings`, data),
  audio: (studentId, submissionId) => apiClient.get(`/students/${studentId}/writing/${submissionId}/audio`),
};

// research dataset: parent consent and admin controls
export const researchAPI = {
  getConsent: (studentId) => apiClient.get(`/students/${studentId}/research-consent`),
  setConsent: (studentId, granted, consent = false) => apiClient.put(`/students/${studentId}/research-consent`, { granted, consent }),
  summary: () => apiClient.get('/admin/research/summary'),
  rebuild: () => apiClient.post('/admin/research/rebuild', null, { timeout: 120000 }),
  aggregates: (metric) => apiClient.get('/admin/research/aggregates', { params: metric ? { metric } : {} }),
  csv: () => apiClient.get('/admin/research/aggregates.csv', { responseType: 'blob' }),
  dictionary: () => apiClient.get('/admin/research/dictionary'),
};

// who may create an account (invite-only pilot)
export const accessAPI = {
  publicAccess: () => apiClient.get('/public/access'),
  adminGet: () => apiClient.get('/admin/access'),
  setMode: (mode, confirmPublic = false) => apiClient.put('/admin/access/mode', { mode, confirm_public: confirmPublic }),
  createCode: (data) => apiClient.post('/admin/access/codes', data),
  revokeCode: (code) => apiClient.delete(`/admin/access/codes/${code}`),
};

// administrator's running list of what is broken and when it was fixed
export const devTasksAPI = {
  version: () => apiClient.get('/admin/app-version'),
  list: (params = {}) => apiClient.get('/admin/devtasks', { params }),
  create: (data) => apiClient.post('/admin/devtasks', data),
  update: (id, data) => apiClient.patch(`/admin/devtasks/${id}`, data),
  changelog: () => apiClient.get('/admin/changelog'),
  addChange: (data) => apiClient.post('/admin/changelog', data),
};

export const bedtimeAPI = {
  start: (data) => apiClient.post('/bedtime-stories/start', data),
  complete: (id) => apiClient.post(`/bedtime-stories/${id}/complete`),
  list: (params) => apiClient.get('/bedtime-stories', { params }),
  play: (id) => apiClient.get(`/bedtime-stories/${id}/play`),
  download: (id) => apiClient.get(`/bedtime-stories/${id}/download`),
  voices: () => apiClient.get('/voices/cloned'),
  aiOptions: () => apiClient.get('/bedtime-stories/ai-voice/options'),
  aiCreate: (data) => apiClient.post('/bedtime-stories/ai-voice', data, { timeout: 150000 }),
  update: (id, data) => apiClient.patch(`/bedtime-stories/${id}`, data),
  share: (id, consent) => apiClient.post(`/bedtime-stories/${id}/share`, { consent }),
  unshare: (id) => apiClient.post(`/bedtime-stories/${id}/unshare`),
  pending: () => apiClient.get('/admin/bedtime-stories/pending'),
  adminPlay: (id) => apiClient.get(`/admin/bedtime-stories/${id}/play`),
  review: (id, approve) => apiClient.post(`/admin/bedtime-stories/${id}/review`, { approve }),
  mine: (params) => apiClient.get('/bedtime-stories/mine', { params }),
  playMine: (id) => apiClient.get(`/bedtime-stories/mine/${id}/play`),
  // Straight to storage with the signed URL: no auth header, no size limit on our API.
  putToStorage: (url, file, contentType, onProgress) =>
    axios.put(url, file, {
      headers: { 'Content-Type': contentType },
      timeout: 0,
      onUploadProgress: (e) => e.total && onProgress && onProgress(Math.round((e.loaded / e.total) * 100)),
    }),
};

export const studentAPI = {
  create: (data) => apiClient.post('/students', data),
  getAll: (guardianId) => apiClient.get('/students', { params: { guardian_id: guardianId } }),
  getById: (id) => apiClient.get(`/students/${id}`),
  update: (id, data) => apiClient.patch(`/students/${id}`, data),
  delete: (id) => apiClient.delete(`/students/${id}`),
  resetPin: (id) => apiClient.post(`/students/${id}/reset-pin`),
  changePin: (id, data) => apiClient.post(`/students/${id}/change-pin`, data),
  changeMyPin: (data) => apiClient.post('/student/change-my-pin', data),
  uploadPhoto: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/students/${id}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getProgress: (id) => apiClient.get(`/students/${id}/progress`),
  getVirtualBackgrounds: (id) => apiClient.get(`/students/${id}/virtual-backgrounds`),
  regenerateVirtualBackgrounds: (id) => apiClient.post(`/students/${id}/virtual-backgrounds/regenerate`),
  getExportUrl: (id, format) => `${API}/students/${id}/export?format=${format}`,
  setReadingGoal: (id, data) => apiClient.put(`/students/${id}/reading-goal`, data),
  removeReadingGoal: (id) => apiClient.delete(`/students/${id}/reading-goal`),
  getReadingGoalProgress: (id) => apiClient.get(`/students/${id}/reading-goal/progress`),
  bulkImport: (file, classroomId) => {
    const fd = new FormData();
    fd.append('file', file);
    if (classroomId) fd.append('classroom_id', classroomId);
    return apiClient.post('/students/bulk-import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getImportTemplateUrl: () => `${API}/students/import-template`,
  linkTeacher: (studentId, teacherUserId) => apiClient.post(`/students/${studentId}/link-teacher`, { teacher_user_id: teacherUserId }),
  linkParent: (studentId, parentEmail) => apiClient.post(`/students/${studentId}/link-parent`, { parent_email: parentEmail }),
  getBySchool: (schoolId) => apiClient.get(`/students/by-school/${schoolId}`),
  toggleSpellcheck: (id) => apiClient.post(`/students/${id}/spellcheck`),
  toggleSpellingMode: (id) => apiClient.post(`/students/${id}/spelling-mode`),
  getSpellingLogs: (id) => apiClient.get(`/students/${id}/spelling-logs`),
  getTimeLog: (id) => apiClient.get(`/students/${id}/time-log`),
  sendInvitations: () => apiClient.post('/students/send-invitations'),
  getEnrollmentTracker: () => apiClient.get('/students/enrollment-tracker'),
  sendReminders: () => apiClient.post('/students/send-reminders'),
  bulkDelete: (studentIds) => apiClient.post('/students/bulk-delete', { student_ids: studentIds }),
};

// ==================== SESSION API ====================

export const sessionAPI = {
  start: (studentId) => apiClient.post('/sessions/start', { student_id: studentId }),
  heartbeat: (studentId, sessionId) => apiClient.post('/sessions/heartbeat', { student_id: studentId, session_id: sessionId }),
  end: (studentId, sessionId) => apiClient.post('/sessions/end', { student_id: studentId, session_id: sessionId }),
};

// ==================== SUBSCRIPTION API ====================

export const subscriptionAPI = {
  get: (guardianId) => apiClient.get(`/subscriptions/${guardianId}`),
  getAvailablePlans: () => apiClient.get('/subscription-plans/available'),
  upgrade: (data) => apiClient.post('/subscriptions/upgrade', data),
};

// ==================== WORD BANK API ====================

export const wordBankAPI = {
  create: (data) => apiClient.post('/word-banks', data),
  getAll: (params) => apiClient.get('/word-banks', { params }),
  getById: (id) => apiClient.get(`/word-banks/${id}`),
  update: (id, data) => apiClient.put(`/word-banks/${id}`, data),
  delete: (id) => apiClient.delete(`/word-banks/${id}`),
  purchase: (guardianId, bankId) => apiClient.post('/word-banks/purchase', { guardian_id: guardianId, bank_id: bankId }),
  assignToStudent: (studentId, bankIds) => apiClient.post('/students/assign-banks', { student_id: studentId, bank_ids: bankIds }),
  canParentCreate: () => apiClient.get('/feature-flags/parent-wordbank'),
};

// ==================== NARRATIVE API ====================

export const narrativeAPI = {
  audioToken: (id) => apiClient.get(`/narratives/${id}/audio-token`),
  create: (data) => apiClient.post('/narratives', data),
  createBatch: (data) => apiClient.post('/narratives/batch', data),
  getAll: (studentId, opts = {}) =>
    apiClient.get('/narratives', {
      params: {
        student_id: studentId,
        ...(opts.includeArchived ? { include_archived: true } : {}),
      },
    }),
  getById: (id) => apiClient.get(`/narratives/${id}`),
  delete: (id) => apiClient.delete(`/narratives/${id}`),
  archive: (id) => apiClient.post(`/narratives/${id}/archive`),
  unarchive: (id) => apiClient.post(`/narratives/${id}/unarchive`),
  reportTooHard: (id) => apiClient.post(`/narratives/${id}/too-hard`),
  difficultyFeedback: (id, feedback) => apiClient.post(`/narratives/${id}/difficulty-feedback`, { feedback }),
};

// ==================== READ LOG API ====================

export const readLogAPI = {
  create: (data) => apiClient.post('/read-logs', data),
  getAll: (studentId) => apiClient.get('/read-logs', { params: { student_id: studentId } }),
};

// ==================== INTEGRITY / ANTI-CHEAT API ====================

export const integrityAPI = {
  heartbeat: (payload) => apiClient.post('/reading/heartbeat', payload),
  closeSession: (payload) => apiClient.post('/reading/session/close', payload),
  listSessions: (studentId, narrativeId) =>
    apiClient.get('/reading/sessions', { params: { student_id: studentId, narrative_id: narrativeId } }),
  pasteEvent: (payload) => apiClient.post('/integrity/paste-event', payload),
  listSignals: (studentId, opts = {}) =>
    apiClient.get('/integrity/signals', { params: { student_id: studentId, ...opts } }),
  signalsSummary: (studentId, sinceDays = 7) =>
    apiClient.get('/integrity/signals/summary', { params: { student_id: studentId, since_days: sinceDays } }),
  getEffectiveRules: (studentId) =>
    apiClient.get('/integrity/rules/effective', { params: { student_id: studentId } }),
  upsertRules: (payload) => apiClient.put('/integrity/rules', payload),
  listRemediation: (studentId, status) =>
    apiClient.get('/integrity/remediation', { params: { student_id: studentId, ...(status ? { status } : {}) } }),
  generateRemediation: (queueId) =>
    apiClient.post(`/integrity/remediation/${queueId}/generate`),
  checkPattern: (studentId) =>
    apiClient.post(`/integrity/remediation/check-pattern/${studentId}`),
};

// ==================== REFLECTION (character questions) ====================

export const reflectionAPI = {
  next: (studentId, narrativeId) =>
    apiClient.get('/reflection/next-question', { params: { student_id: studentId, narrative_id: narrativeId } }),
  submit: (payload) => apiClient.post('/reflection/answers', payload),
  list: (studentId) =>
    apiClient.get('/reflection/answers', { params: { student_id: studentId } }),
};

// ==================== ASSESSMENT API ====================

export const assessmentAPI = {
  create: (data) => apiClient.post('/assessments', data),
  getAll: (params) => apiClient.get('/assessments', { params }),
  getById: (id) => apiClient.get(`/assessments/${id}`),
  evaluate: (id, answers) => apiClient.post(`/assessments/${id}/evaluate`, answers),
  evaluateWritten: (data) => apiClient.post('/assessments/evaluate-written', data),
};

// ==================== GIFT API ====================

export const giftAPI = {
  create: (data) => apiClient.post('/gifts', data),
  redeem: (token) => apiClient.post('/gifts/redeem', { token }),
};

// ==================== CLASSROOM MANAGE API ====================

export const classroomManageAPI = {
  create: (data) => apiClient.post('/classrooms', data),
  getAll: () => apiClient.get('/classrooms'),
  getById: (id) => apiClient.get(`/classrooms/${id}`),
  update: (id, data) => apiClient.patch(`/classrooms/${id}`, data),
  delete: (id, deleteStudents = false) => apiClient.delete(`/classrooms/${id}`, { params: { delete_students: deleteStudents } }),
  addStudents: (id, studentIds) => apiClient.post(`/classrooms/${id}/students`, { student_ids: studentIds }),
  removeStudent: (id, studentId) => apiClient.delete(`/classrooms/${id}/students/${studentId}`),
  assignBanks: (id, bankIds) => apiClient.post(`/classrooms/${id}/assign-banks`, { bank_ids: bankIds }),
  getAnalytics: (id) => apiClient.get(`/classrooms/${id}/analytics`),
};

// ==================== CLASSROOM SESSION API ====================

export const classroomAPI = {
  create: (data) => apiClient.post('/classroom-sessions', data),
  getAll: () => apiClient.get('/classroom-sessions'),
  getById: (id) => apiClient.get(`/classroom-sessions/${id}`),
  start: (id) => apiClient.post(`/classroom-sessions/${id}/start`),
  end: (id) => apiClient.post(`/classroom-sessions/${id}/end`),
  join: (code, studentId) => apiClient.post('/classroom-sessions/join', { session_code: code, student_id: studentId }),
  analytics: (id) => apiClient.get(`/classroom-sessions/${id}/analytics`),
  goLive: (id) => apiClient.post(`/classroom-sessions/${id}/go-live`),
  liveInfo: (id) => apiClient.get(`/classroom-sessions/${id}/live-info`),
  endLive: (id) => apiClient.post(`/classroom-sessions/${id}/end-live`),
};

// ==================== ADMIN API ====================

export const adminAPI = {
  getCosts: () => apiClient.get('/admin/costs'),
  getPricingCalculator: () => apiClient.get('/admin/pricing-calculator'),
  getModels: () => apiClient.get('/admin/models'),
  updateModels: (data) => apiClient.post('/admin/models', data),
  getTTSConfig: () => apiClient.get('/admin/tts-config'),
  updateTTSConfig: (data) => apiClient.put('/admin/tts-config', data),
  getSettings: () => apiClient.get('/admin/settings'),
  updateSettings: (data) => apiClient.post('/admin/settings', data),
  getStats: () => apiClient.get('/admin/stats'),
  getUsers: (search) => apiClient.get('/admin/users', { params: search ? { search } : {} }),
  delegateAdmin: (data) => apiClient.post('/admin/delegate', data),
  getCoupons: () => apiClient.get('/admin/coupons'),
  createCoupon: (data) => apiClient.post('/admin/coupons', data),
  deleteCoupon: (id) => apiClient.delete(`/admin/coupons/${id}`),
  getPlans: () => apiClient.get('/admin/plans'),
  createPlan: (data) => apiClient.post('/admin/plans', data),
  deletePlan: (id) => apiClient.delete(`/admin/plans/${id}`),
  updatePlan: (id, data) => apiClient.put(`/admin/plans/${id}`, data),
  assignSubscription: (userId, data) => apiClient.post(`/admin/users/${userId}/assign-subscription`, data),
  createWordBank: (data) => apiClient.post('/admin/word-banks', data),
  getBillingConfig: () => apiClient.get('/admin/billing-config'),
  updateBillingConfig: (data) => apiClient.post('/admin/billing-config', data),
  getFeatureFlags: () => apiClient.get('/admin/feature-flags'),
  updateFeatureFlags: (data) => apiClient.post('/admin/feature-flags', data),
  getBrands: () => apiClient.get('/admin/brands'),
  createBrand: (data) => apiClient.post('/admin/brands', data),
  updateBrand: (id, data) => apiClient.put(`/admin/brands/${id}`, data),
  deleteBrand: (id) => apiClient.delete(`/admin/brands/${id}`),
  getBrandAnalytics: () => apiClient.get('/admin/brand-analytics'),
  getClassroomSponsorships: () => apiClient.get('/admin/classroom-sponsorships'),
  createClassroomSponsorship: (data) => apiClient.post('/admin/classroom-sponsorships', data),
  deleteClassroomSponsorship: (id) => apiClient.delete(`/admin/classroom-sponsorships/${id}`),
  // User Management
  createUser: (data) => apiClient.post('/admin/users', data),
  updateUser: (id, data) => apiClient.put(`/admin/users/${id}`, data),
  resetPassword: (id) => apiClient.post(`/admin/users/${id}/reset-password`),
  deactivateUser: (id) => apiClient.post(`/admin/users/${id}/deactivate`),
  deleteUser: (id) => apiClient.delete(`/admin/users/${id}`),
  addCredits: (id, data) => apiClient.post(`/admin/users/${id}/add-credits`, data),
  getPlanStats: () => apiClient.get('/admin/plan-stats'),
  editUserSubscription: (userId, data) => apiClient.put(`/admin/users/${userId}/subscription`, data),
  editUserWallet: (userId, data) => apiClient.put(`/admin/users/${userId}/wallet`, data),
  // Integrations / API Keys
  getIntegrations: () => apiClient.get('/admin/integrations'),
  updateIntegrations: (data) => apiClient.put('/admin/integrations', data),
  // Impersonation
  impersonateUser: (userId) => apiClient.post(`/admin/impersonate/${userId}`),
  // Support Sessions (Daily.co)
  createSupportSession: () => apiClient.post('/admin/support-session'),
  getSupportSessions: () => apiClient.get('/admin/support-sessions'),
  // Digital Media
  getMediaSettings: () => apiClient.get('/admin/media-settings'),
  updateMediaSettings: (data) => apiClient.put('/admin/media-settings', data),
  listBrandMedia: () => apiClient.get('/admin/brand-media'),
  createBrandMedia: (data) => apiClient.post('/admin/brand-media', data),
  uploadBrandMedia: (formData) => apiClient.post('/admin/brand-media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateBrandMedia: (id, data) => apiClient.put(`/admin/brand-media/${id}`, data),
  deleteBrandMedia: (id) => apiClient.delete(`/admin/brand-media/${id}`),
  getStorageStats: () => apiClient.get('/admin/storage-stats'),
};

// ==================== WALLET API ====================

export const walletAPI = {
  getBalance: () => apiClient.get('/wallet/balance'),
  getTransactions: () => apiClient.get('/wallet/transactions'),
  getPackages: () => apiClient.get('/wallet/packages'),
  topup: (data) => apiClient.post('/wallet/topup', data),
  purchaseBank: (data) => apiClient.post('/wallet/purchase-bank', data),
  getPaymentStatus: (sessionId) => apiClient.get(`/payments/status/${sessionId}`),
};

// ==================== PAYPAL API ====================

export const paypalAPI = {
  getConfig: () => apiClient.get('/paypal/config'),
  createOrder: (data) => apiClient.post('/paypal/create-order', data),
  captureOrder: (orderId) => apiClient.post(`/paypal/capture-order/${orderId}`),
};

// ==================== USER CARD API ====================

export const userCardAPI = {
  getCardData: () => apiClient.get('/user-card'),
};


// ==================== COUPON API ====================

export const couponAPI = {
  redeem: (code) => apiClient.post('/coupons/redeem', { code }),
};

// ==================== REFERRAL API ====================

export const referralAPI = {
  getMyCode: () => apiClient.get('/referrals/my-code'),
  getMyReferrals: () => apiClient.get('/referrals/my-referrals'),
  getRewardAmount: () => apiClient.get('/referrals/reward-amount'),
  getActiveContest: () => apiClient.get('/contests/active'),
  getLeaderboard: (contestId) => apiClient.get('/contests/leaderboard', { params: contestId ? { contest_id: contestId } : {} }),
};

// ==================== WORD DEFINITION API ====================

export const wordAPI = {
  define: (word, context) => apiClient.post('/words/define', { word, context }),
};

// ==================== DONATION API ====================

export const donationAPI = {
  create: (data) => apiClient.post('/donations/create', data),
  getStatus: (sessionId) => apiClient.get(`/donations/status/${sessionId}`),
  getStats: () => apiClient.get('/donations/stats'),
};

// ==================== STUDENT AD PREFERENCES API ====================

export const adPreferencesAPI = {
  get: (studentId) => apiClient.get(`/students/${studentId}/ad-preferences`),
  update: (studentId, data) => apiClient.post(`/students/${studentId}/ad-preferences`, data),
};

// ==================== ARTIST API ====================

export const artistAPI = {
  getProfile: () => apiClient.get('/artist/profile'),
  updateProfile: (data) => apiClient.put('/artist/profile', data),
  getSongs: () => apiClient.get('/artist/songs'),
};

export const artistAdminAPI = {
  listArtists: () => apiClient.get('/admin/artists'),
  listLogins: () => apiClient.get('/admin/artist-logins'),
  createArtist: (data) => apiClient.post('/admin/artists', data),
  setStatus: (id, status) => apiClient.post(`/admin/artists/${id}/status`, null, { params: { status } }),
  assign: (id, userId) => apiClient.post(`/admin/artists/${id}/assign`, null, { params: { user_id: userId } }),
  listSongs: () => apiClient.get('/catalog/songs'),
  setSongStatus: (id, status) => apiClient.post(`/catalog/songs/${id}/status`, new URLSearchParams({ status })),
  setSongAccess: (id, plan, minAge, ask) => apiClient.post(`/catalog/songs/${id}/access`, new URLSearchParams({ required_plan: plan, min_age: String(minAge), requires_permission: String(ask) })),
  takedownPreview: (kind, id) => apiClient.get('/admin/takedown-preview', { params: { kind, id } }),
  takedown: (kind, id, reason) => apiClient.post('/admin/takedown', { kind, id, reason }),
  sync: (data) => apiClient.post('/catalog/sync', data, { timeout: 180000 }),
  audit: () => apiClient.get('/catalog/audit'),
};

// ==================== BRAND PORTAL API ====================

export const brandPortalAPI = {
  getProfile: () => apiClient.get('/brand-portal/profile'),
  getDashboard: () => apiClient.get('/brand-portal/dashboard'),
  updateProfile: (data) => apiClient.put('/brand-portal/profile', data),
  uploadLogo: (formData) => apiClient.post('/brand-portal/logo-upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getProducts: () => apiClient.get('/brand-portal/products'),
  addProduct: (data) => apiClient.post('/brand-portal/products', data),
  updateProduct: (id, data) => apiClient.put(`/brand-portal/products/${id}`, data),
  deleteProduct: (id) => apiClient.delete(`/brand-portal/products/${id}`),
  getCampaigns: () => apiClient.get('/brand-portal/campaigns'),
  createCampaign: (data) => apiClient.post('/brand-portal/campaigns', data),
  updateCampaign: (id, data) => apiClient.put(`/brand-portal/campaigns/${id}`, data),
  deleteCampaign: (id) => apiClient.delete(`/brand-portal/campaigns/${id}`),
  topup: (data) => apiClient.post('/brand-portal/topup', data),
  getTopupStatus: (sessionId) => apiClient.get(`/brand-portal/topup-status/${sessionId}`),
  getStoryPreview: () => apiClient.get('/brand-portal/story-preview'),
  generateStoryPreview: () => apiClient.post('/brand-portal/story-preview'),
  getStoryIntegrations: () => apiClient.get('/brand-portal/story-integrations'),
  getStoryDetail: (narrativeId) => apiClient.get(`/brand-portal/story/${narrativeId}`),
  getAnalytics: () => apiClient.get('/brand-portal/analytics'),
  getCoupons: () => apiClient.get('/brand-portal/coupons'),
  createCoupon: (data) => apiClient.post('/brand-portal/coupons', data),
  deleteCoupon: (id) => apiClient.delete(`/brand-portal/coupons/${id}`),
};

// ==================== AFFILIATE API ====================

export const affiliateAPI = {
  signup: (data) => apiClient.post('/affiliates/signup', data),
  track: (code) => apiClient.get(`/affiliates/track/${code}`),
  getMyStats: () => apiClient.get('/affiliates/my-stats'),
};

// ==================== ADMIN AFFILIATE API ====================

export const adminAffiliateAPI = {
  getAll: () => apiClient.get('/admin/affiliates'),
  updateSettings: (data) => apiClient.put('/admin/affiliates/settings', data),
  update: (id, data) => apiClient.put(`/admin/affiliates/${id}`, data),
  payout: (id, data) => apiClient.post(`/admin/affiliates/${id}/payout`, data),
};

// ==================== BRAND OFFERS API ====================

export const brandOffersAPI = {
  create: (data) => apiClient.post('/brands/offers', data),
  getOwn: () => apiClient.get('/brands/offers'),
  update: (id, data) => apiClient.put(`/brands/offers/${id}`, data),
  remove: (id) => apiClient.delete(`/brands/offers/${id}`),
};

export const parentOffersAPI = {
  getAvailable: () => apiClient.get('/offers'),
  updatePreferences: (data) => apiClient.put('/offers/preferences', data),
  trackClick: (id) => apiClient.post(`/offers/${id}/click`),
};

export const recordingsAPI = {
  upload: (formData) => apiClient.post('/recordings/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }),
  analyze: (id) => apiClient.post(`/recordings/${id}/analyze`),
  getForStudent: (studentId) => apiClient.get(`/recordings/student/${studentId}`),
  getGuardianAll: (params) => apiClient.get('/recordings/guardian/all', { params }),
  stream: (id) => `${apiClient.defaults.baseURL}/recordings/${id}/stream`,
  // <audio>/<video> cannot send a login header, so playback uses a short-lived signed link.
  streamUrl: async (id) => {
    const { data } = await apiClient.get(`/recordings/${id}/stream-link`);
    return `${apiClient.defaults.baseURL}/recordings/${id}/stream?t=${encodeURIComponent(data.token)}`;
  },
  download: (id) => apiClient.get(`/recordings/${id}/download`),
  delete: (id) => apiClient.delete(`/recordings/${id}`),
  getProgress: (studentId) => apiClient.get(`/recordings/student/${studentId}/progress`),
  getNotes: (id) => apiClient.get(`/recordings/${id}/notes`),
  addNote: (id, data) => apiClient.post(`/recordings/${id}/notes`, data),
  deleteNote: (recordingId, noteId) => apiClient.delete(`/recordings/${recordingId}/notes/${noteId}`),
  createShareLink: (id) => apiClient.post(`/recordings/${id}/share`, { consent: true }),
  getShared: (token) => axios.get(`${API}/recordings/shared/${token}`),
};

export const audioBooksAPI = {
  contribute: (data) => apiClient.post('/audio-books/contribute', data),
  getAll: (page = 1) => apiClient.get(`/audio-books?page=${page}`),
  stream: (id) => `${apiClient.defaults.baseURL}/audio-books/${id}/stream`,
  like: (id) => apiClient.post(`/audio-books/${id}/like`),
  adminGetAll: () => apiClient.get('/admin/audio-books'),
  adminUpdate: (id, data) => apiClient.put(`/admin/audio-books/${id}`, data),
  adminDelete: (id) => apiClient.delete(`/admin/audio-books/${id}`),
  adminGetSettings: () => apiClient.get('/admin/audio-books/settings'),
  adminUpdateSettings: (data) => apiClient.put('/admin/audio-books/settings', data),
};

export const messagesAPI = {
  adminCreate: (data) => apiClient.post('/admin/messages', data),
  adminList: () => apiClient.get('/admin/messages'),
  adminDelete: (id) => apiClient.delete(`/admin/messages/${id}`),
  getNotifications: () => apiClient.get('/notifications'),
  getStudentNotifications: (studentId) => apiClient.get(`/student-notifications/${studentId}`),
  markRead: (id) => apiClient.post(`/notifications/${id}/read`),
  markStudentRead: (id, studentId) => apiClient.post(`/student-notifications/${id}/read`, { student_id: studentId }),
};

export const parentalControlsAPI = {
  get: (studentId) => apiClient.get(`/students/${studentId}/parental-controls`),
  update: (studentId, data) => apiClient.put(`/students/${studentId}/parental-controls`, data),
};

export const mediaAPI = {
  getStudentLibrary: (studentId) => apiClient.get(`/students/${studentId}/media-library`),
  recordListen: (studentId, mediaId) => apiClient.post(`/students/${studentId}/media-listen?media_id=${mediaId}`),
  toggleLike: (studentId, data) => apiClient.post(`/students/${studentId}/media-like`, data),
  purchaseDownload: (studentId, data) => apiClient.post(`/students/${studentId}/media-download`, data),
  getMediaForStory: (studentId) => apiClient.get(`/brand-media/for-story/${studentId}`),
  getChildrenMedia: () => apiClient.get('/guardian/children-media'),
  getCatalog: (studentId) => apiClient.get(`/students/${studentId}/media-catalog`),
  setPermission: (studentId, mediaId, allowed) => apiClient.post(`/students/${studentId}/media-permission`, null, { params: { media_id: mediaId, allowed } }),
  updateMediaPreference: (studentId, enabled) => apiClient.post(`/students/${studentId}/media-preference?enabled=${enabled}`),
};

// Songs only play with a short-lived signed address, handed out to signed-in users.
// Turns '/api/media/gridfs/<id>' into '<base>/api/media/gridfs/<id>?t=<token>'. Anything else is returned unchanged.
export async function resolveMediaSrc(fileUrl) {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const m = /\/media\/gridfs\/([^/?#]+)/.exec(fileUrl || '');
  if (!m) return `${base}${fileUrl || ''}`;
  try {
    const { data } = await apiClient.get(`/media/${m[1]}/play-url`);
    return `${base}${data.url}`;
  } catch (e) {
    // a locked song comes back with a note for the listener
    const err = new Error('locked');
    err.userMessage = e.response?.data?.detail?.message || 'This song is not available right now.';
    throw err;
  }
}

export const musicCreditsAPI = {
  getStudentCredits: (studentId) => apiClient.get(`/students/${studentId}/music-credits`),
  getGuardianCredits: () => apiClient.get('/guardian/music-credits'),
  allocate: (data) => apiClient.post('/guardian/music-credits/allocate', data),
  autoSplit: () => apiClient.post('/guardian/music-credits/auto-split'),
  topup: (data) => apiClient.post('/guardian/music-credits/topup', data),
  transfer: (data) => apiClient.post('/guardian/music-credits/transfer', data),
  getPendingTransfers: () => apiClient.get('/music-credits/pending-transfers'),
  acceptTransfer: (data) => apiClient.post('/music-credits/accept-transfer', data),
  declineTransfer: (data) => apiClient.post('/music-credits/decline-transfer', data),
};

export const chatAPI = {
  getMyRooms: () => apiClient.get('/chat/my-rooms'),
  getStatus: (classroomId) => apiClient.get(`/chat/${classroomId}/status`),
  getMessages: (classroomId, limit = 50) => apiClient.get(`/chat/${classroomId}/messages?limit=${limit}`),
  sendMessage: (data) => apiClient.post('/chat/send', data),
  toggleClassroomChat: (classroomId, enabled) => apiClient.put(`/classrooms/${classroomId}/chat`, { chat_enabled: enabled }),
  toggleStudentChat: (studentId, enabled) => apiClient.put(`/students/${studentId}/chat`, { chat_enabled: enabled }),
  getAdminSettings: () => apiClient.get('/admin/chat-settings'),
  updateAdminSettings: (data) => apiClient.put('/admin/chat-settings', data),
  adminGetMessages: (classroomId, limit) => apiClient.get(`/admin/chat/messages?classroom_id=${classroomId || ''}&limit=${limit || 100}`),
  adminDeleteMessage: (messageId) => apiClient.delete(`/admin/chat/messages/${messageId}`),
};

export const remindersAPI = {
  getStudentReminders: (studentId) => apiClient.get(`/student-reminders/${studentId}`),
  checkMilestones: (studentId) => apiClient.post(`/parent-milestone-check/${studentId}`),
};

export const spellingContestsAPI = {
  adminCreate: (data) => apiClient.post('/admin/spelling-contests', data),
  adminList: () => apiClient.get('/admin/spelling-contests'),
  adminToggle: (id) => apiClient.put(`/admin/spelling-contests/${id}/toggle`),
  adminDelete: (id) => apiClient.delete(`/admin/spelling-contests/${id}`),
  listActive: () => apiClient.get('/spelling-contests'),
  submit: (data) => apiClient.post('/spelling-contests/submit', data),
  leaderboard: (id) => apiClient.get(`/spelling-contests/${id}/leaderboard`),
};

export const supportAPI = {
  createTicket: (data) => apiClient.post('/support/tickets', data),
  addAttachment: (ticketId, formData) => apiClient.post(`/support/tickets/${ticketId}/attachment`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listTickets: () => apiClient.get('/support/tickets'),
  getTicket: (id) => apiClient.get(`/support/tickets/${id}`),
  adminReply: (ticketId, data) => apiClient.post(`/support/tickets/${ticketId}/reply`, data),
  updateStatus: (ticketId, data) => apiClient.put(`/support/tickets/${ticketId}/status`, data),
};

export const narrativeProgressAPI = {
  save: (data) => apiClient.post('/narratives/save-progress', data),
  get: (narrativeId) => apiClient.get(`/narratives/${narrativeId}/progress`),
  getStats: (narrativeId) => apiClient.get(`/narratives/${narrativeId}/stats`),
  getReportUrl: (narrativeId) => `${API}/narratives/${narrativeId}/report`,
};

export const readingBeeAPI = {
  create: (data) => apiClient.post('/reading-bees', data),
  getAll: (opts = {}) =>
    apiClient.get('/reading-bees', {
      params: opts.includeArchived ? { include_archived: true } : {},
    }),
  archive: (id) => apiClient.post(`/reading-bees/${id}/archive`),
  unarchive: (id) => apiClient.post(`/reading-bees/${id}/unarchive`),
  getById: (id) => apiClient.get(`/reading-bees/${id}`),
  submitEntry: (id, data) => apiClient.post(`/reading-bees/${id}/entries`, data),
  vote: (id, data) => apiClient.post(`/reading-bees/${id}/vote`, data),
  getResults: (id) => apiClient.get(`/reading-bees/${id}/results`),
  participation: (id) => apiClient.get(`/reading-bees/${id}/participation`),
  openStory: (id, studentId) => apiClient.post(`/reading-bees/${id}/open-story`, { student_id: studentId }),
  update: (id, data) => apiClient.put(`/reading-bees/${id}`, data),
  delete: (id) => apiClient.delete(`/reading-bees/${id}`),
};

// ==================== SCREEN SHARE API ====================

export const screenShareAPI = {
  createSession: (targetUserId) => apiClient.post('/screen-share/sessions', { target_user_id: targetUserId || null }),
  listSessions: () => apiClient.get('/screen-share/sessions'),
  joinByCode: (code) => apiClient.get(`/screen-share/join/${code}`),
  endSession: (sessionId) => apiClient.post(`/screen-share/sessions/${sessionId}/end`),
};

export const backupAPI = {
  getStatus: () => apiClient.get('/admin/backup/status'),
  download: (customFilename) => {
    const token = localStorage.getItem('token');
    return fetch(`${API}/admin/backup`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => {
      if (!res.ok) throw new Error('Backup failed');
      return res.blob().then(blob => {
        const downloadName = customFilename
          ? (customFilename.endsWith('.json') ? customFilename : `${customFilename}.json`)
          : 'backup.json';
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = downloadName;
        a.click();
        URL.revokeObjectURL(a.href);
      });
    });
  },
  getInfo: () => apiClient.get('/admin/backup/info'),
  restore: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/admin/restore', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
};

// ==================== ACTIVITY LOG API ====================

export const activityLogAPI = {
  log: (data) => apiClient.post('/activity-log', data),
  getAll: (params) => apiClient.get('/admin/activity-logs', { params }),
  getUserSessions: (userId) => apiClient.get(`/admin/user-sessions/${userId}`),
};

export default apiClient;
